import './OrderSummaryAside.scss';

import React, { useMemo, useState } from 'react';

import { PatientOrderingProduct } from '@customTypes';

import { canSelectProduct, getAllDeselectedProductIds, getRequiredProducts } from '../utils/productSelection';
import { OrderSummaryAddProduct, OrderSummaryRemoveProduct } from './OrderSummaryChip';
import { ProductConfirmationModal } from './ProductConfirmationModal';

export interface OrderSummaryAsideProps {
  products: PatientOrderingProduct[];
  selectedProductIds: string[];
  onDeselectProduct: (productId: string) => void;
  onSelectProduct: (productId: string) => void;
}

export const OrderSummaryAside = (props: OrderSummaryAsideProps) => {
  const { products, selectedProductIds, onDeselectProduct, onSelectProduct } = props;

  const [pendingConfirmation, setPendingConfirmation] = useState<PatientOrderingProduct | null>(null);

  const sortedProducts = useMemo(() => {
    const _sortedProducts = products.filter(p => !p.requires_product_ids?.length);
    const _dependentProducts = products.filter(p => p.requires_product_ids?.length);

    _dependentProducts.forEach(dependent => {
      const anchorIndex = _sortedProducts.findIndex(p => dependent.requires_product_ids?.includes(p.product_id));
      const insertAt = anchorIndex === -1 ? _sortedProducts.length : anchorIndex + 1;
      _sortedProducts.splice(insertAt, 0, dependent);
    });

    return _sortedProducts;
  }, [products]);

  const selected = sortedProducts.filter(product => selectedProductIds.includes(product.product_id));
  const available = sortedProducts.filter(product => !selectedProductIds.includes(product.product_id));

  const handleAddClick = (product: PatientOrderingProduct) => {
    if (!canSelectProduct(product, selectedProductIds)) {
      return;
    }

    if (product.requires_confirmation) {
      setPendingConfirmation(product);
      return;
    }

    onSelectProduct(product.product_id);
  };

  if (!products?.length) {
    return <aside className='order-summary-aside' />;
  }

  return (
    <aside className='order-summary-aside'>
      <div className='order-summary-aside__header'>
        <h2 className='order-summary-aside__title'>You&apos;re ordering</h2>
      </div>
      <div className='order-summary-aside__chips'>
        {selected.map(product => {
          // `deselectIds` includes this product and any dependents this removal would take with it.
          const deselectIds = getAllDeselectedProductIds(sortedProducts, selectedProductIds, product.product_id);
          const disabled = selected.length - deselectIds.length <= 0;

          return (
            <OrderSummaryRemoveProduct
              key={product.product_id}
              product={product}
              disabled={disabled}
              onClick={() => onDeselectProduct(product.product_id)}
            />
          );
        })}
      </div>

      {available.length > 0 && (
        <>
          <h3 className='order-summary-aside__subtitle'>Available to add</h3>

          <div className='order-summary-aside__chips'>
            {available.map(product => {
              return (
                <OrderSummaryAddProduct
                  key={product.product_id}
                  product={product}
                  requiredProducts={getRequiredProducts(product, sortedProducts)}
                  disabled={!canSelectProduct(product, selectedProductIds)}
                  onClick={() => handleAddClick(product)}
                />
              );
            })}
          </div>
        </>
      )}

      {pendingConfirmation && (
        <ProductConfirmationModal
          product={pendingConfirmation}
          requiredProducts={getRequiredProducts(pendingConfirmation, sortedProducts)}
          onConfirm={() => {
            onSelectProduct(pendingConfirmation.product_id);
            setPendingConfirmation(null);
          }}
          onCancel={() => setPendingConfirmation(null)}
        />
      )}
    </aside>
  );
};
